import { useNavigate } from 'react-router';
import { useState, useCallback } from 'react';

import { MESSAGES } from 'src/constants/messages';
import { useComboCoveragesById } from 'src/api/hooks/useEmployerDataQueries';
import {
  useMoveCoverageToNewComboMutation,
  useMoveCoverageToExistingComboMutation,
  useMoveCoveragesToNewComboBatchMutation,
  useMoveCoveragesToExistingComboBatchMutation,
} from 'src/api/hooks/useCoverageMutations';

import { toast } from 'src/components/snackbar';

import { getMoveCoverageErrorMessage } from '../utils/getMoveCoverageErrorMessage';

// ----------------------------------------------------------------------

type MoveType = 'existing' | 'new-combo' | null;

const INVALID_COMBO_ID_ERROR = 'Enter a valid combo ID';
const REQUIRED_MOVE_OPTION_ERROR = 'Select a move option.';
const REQUIRED_REASON_ERROR = 'Reason is required.';

type UseMoveCoverageFormProps = {
  comboId: number;
  coverageId?: number;
  coverageIds?: number[];
  onClose: () => void;
  onBatchMoveSuccess?: () => void;
  originPage?: 'coverage' | 'combo';
};

export function useMoveCoverageForm({
  comboId,
  coverageId,
  coverageIds,
  onClose,
  onBatchMoveSuccess,
  originPage = 'coverage',
}: UseMoveCoverageFormProps) {
  const navigate = useNavigate();

  const isBatch = Boolean(coverageIds && coverageIds.length > 1);
  const effectiveCoverageId = coverageId ?? coverageIds?.[0] ?? 0;

  const [moveType, setMoveType] = useState<MoveType>(null);
  const [targetComboIdInput, setTargetComboIdInput] = useState('');
  const [reason, setReason] = useState('');
  const [moveTypeError, setMoveTypeError] = useState('');
  const [targetComboIdError, setTargetComboIdError] = useState('');
  const [reasonError, setReasonError] = useState('');

  const moveToExistingComboMutation = useMoveCoverageToExistingComboMutation();
  const moveToNewComboMutation = useMoveCoverageToNewComboMutation();
  const moveBatchToExistingComboMutation = useMoveCoveragesToExistingComboBatchMutation();
  const moveBatchToNewComboMutation = useMoveCoveragesToNewComboBatchMutation();
  const isSaving =
    moveToExistingComboMutation.isPending ||
    moveToNewComboMutation.isPending ||
    moveBatchToExistingComboMutation.isPending ||
    moveBatchToNewComboMutation.isPending;

  const numericTargetComboId = targetComboIdInput.length === 7 ? Number(targetComboIdInput) : 0;
  const targetComboQuery = useComboCoveragesById(numericTargetComboId);
  const resolvedTargetComboId = targetComboQuery.data?.data?.comboId;
  const resolvedTargetComboName =
    targetComboQuery.data?.data?.controllingCoverage?.comboName?.trim() ?? '';

  const isTargetComboResolved =
    moveType === 'existing' &&
    targetComboIdInput.length === 7 &&
    resolvedTargetComboId === numericTargetComboId;

  const comboLookupFailed =
    moveType === 'existing' &&
    targetComboIdInput.length === 7 &&
    !targetComboQuery.isLoading &&
    (targetComboQuery.isError || resolvedTargetComboId !== numericTargetComboId);

  const comboValidationError = comboLookupFailed ? INVALID_COMBO_ID_ERROR : targetComboIdError;

  const destinationComboPlaceholder =
    moveType === 'existing' ? targetComboIdInput || '##' : 'new combo';

  const systemNotePrefix =
    moveType === null
      ? ''
      : `Coverage moved from Combo ${comboId} to ${destinationComboPlaceholder}.`;

  const successToastMessage = isBatch
    ? `${coverageIds!.length} coverages were successfully moved`
    : MESSAGES.COVERAGE.MOVE_MODAL.SUCCESS_TOAST;

  // builds destination path based on origin page and move result
  const buildDestinationPath = useCallback(
    (destinationComboId: number, primaryCoverageId: number) =>
      originPage === 'combo'
        ? `/dashboard/employer-data/combo/${destinationComboId}`
        : `/dashboard/employer-data/combo/${destinationComboId}/coverage/${primaryCoverageId}`,
    [originPage]
  );

  const resetForm = useCallback(() => {
    setMoveType(null);
    setTargetComboIdInput('');
    setReason('');
    setMoveTypeError('');
    setTargetComboIdError('');
    setReasonError('');
  }, []);

  const validateMoveType = useCallback(
    (showRequiredError: boolean): boolean => {
      if (moveType !== null) {
        setMoveTypeError('');
        return true;
      }

      if (showRequiredError) setMoveTypeError(REQUIRED_MOVE_OPTION_ERROR);
      return false;
    },
    [moveType]
  );

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const validateTargetCombo = useCallback(
    (showRequiredError: boolean): boolean => {
      if (moveType !== 'existing') return true;

      if (targetComboIdInput.length !== 7) {
        if (showRequiredError) setTargetComboIdError(INVALID_COMBO_ID_ERROR);
        return false;
      }

      if (Number(targetComboIdInput) === comboId) {
        setTargetComboIdError(INVALID_COMBO_ID_ERROR);
        return false;
      }

      if (!isTargetComboResolved) {
        setTargetComboIdError(INVALID_COMBO_ID_ERROR);
        return false;
      }

      setTargetComboIdError('');
      return true;
    },
    [comboId, isTargetComboResolved, moveType, targetComboIdInput]
  );

  const validateReason = useCallback(
    (showRequiredError: boolean): boolean => {
      if (!reason.trim()) {
        if (showRequiredError) setReasonError(REQUIRED_REASON_ERROR);
        return false;
      }
      setReasonError('');
      return true;
    },
    [reason]
  );

  const handleTargetComboIdChange = useCallback((value: string) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 7);
    setTargetComboIdInput(sanitized);
    setTargetComboIdError('');
  }, []);

  const handleClearTargetComboId = useCallback(() => {
    setTargetComboIdInput('');
    setTargetComboIdError('');
  }, []);

  const handleReasonChange = useCallback(
    (value: string) => {
      setReason(value);
      if (reasonError) setReasonError('');
    },
    [reasonError]
  );

  const handleMoveTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMoveType(event.target.value as MoveType);
    setMoveTypeError('');
    setTargetComboIdInput('');
    setTargetComboIdError('');
    setReasonError('');
  }, []);

  const handleSave = useCallback(() => {
    if (isSaving) return;

    const hasMoveType = validateMoveType(true);
    const hasReason = validateReason(true);
    const hasValidTarget = moveType === 'existing' ? validateTargetCombo(true) : true;

    if (!hasMoveType || !hasValidTarget || !hasReason) return;

    const note = reason.trim();

    if (moveType === 'existing') {
      if (isBatch) {
        moveBatchToExistingComboMutation.mutate(
          { comboId, coverageIds: coverageIds!, targetComboId: Number(targetComboIdInput), note },
          {
            onSuccess: (data) => {
              toast.success(successToastMessage);
              resetForm();
              onClose();
              onBatchMoveSuccess?.();
              navigate(buildDestinationPath(data.destinationComboId, data.primaryCoverageId), {
                replace: true,
              });
            },
            onError: (error) => toast.error(getMoveCoverageErrorMessage(error)),
          }
        );
      } else {
        moveToExistingComboMutation.mutate(
          {
            comboId,
            coverageId: effectiveCoverageId,
            targetComboId: Number(targetComboIdInput),
            note,
          },
          {
            onSuccess: (data) => {
              if (data.newComboId === undefined || data.newCoverageId === undefined) {
                toast.error(getMoveCoverageErrorMessage(new Error('Move response did not include a destination.')));
                return;
              }
              toast.success(successToastMessage);
              resetForm();
              onClose();
              navigate(buildDestinationPath(data.newComboId, data.newCoverageId), {
                replace: true,
              });
            },
            onError: (error) => toast.error(getMoveCoverageErrorMessage(error)),
          }
        );
      }
      return;
    }

    if (isBatch) {
      moveBatchToNewComboMutation.mutate(
        { comboId, coverageIds: coverageIds!, note },
        {
          onSuccess: (data) => {
            toast.success(successToastMessage);
            resetForm();
            onClose();
            onBatchMoveSuccess?.();
            navigate(buildDestinationPath(data.destinationComboId, data.primaryCoverageId), {
              replace: true,
            });
          },
          onError: (error) => toast.error(getMoveCoverageErrorMessage(error)),
        }
      );
    } else {
      moveToNewComboMutation.mutate(
        { comboId, coverageId: effectiveCoverageId, note },
        {
          onSuccess: (data) => {
            if (data.newComboId === undefined || data.newCoverageId === undefined) {
              toast.error(getMoveCoverageErrorMessage(new Error('Move response did not include a destination.')));
              return;
            }
            toast.success(successToastMessage);
            resetForm();
            onClose();
            navigate(buildDestinationPath(data.newComboId, data.newCoverageId), { replace: true });
          },
          onError: (error) => toast.error(getMoveCoverageErrorMessage(error)),
        }
      );
    }
  }, [
    buildDestinationPath,
    comboId,
    coverageIds,
    effectiveCoverageId,
    isBatch,
    isSaving,
    moveBatchToExistingComboMutation,
    moveBatchToNewComboMutation,
    moveToExistingComboMutation,
    moveToNewComboMutation,
    moveType,
    navigate,
    onBatchMoveSuccess,
    onClose,
    reason,
    resetForm,
    successToastMessage,
    targetComboIdInput,
    validateMoveType,
    validateReason,
    validateTargetCombo,
  ]);

  return {
    isBatch,
    moveType,
    moveTypeError,
    targetComboIdInput,
    reason,
    comboValidationError,
    reasonError,
    isSaving,
    isTargetComboResolved,
    resolvedTargetComboName,
    isComboLookupLoading: targetComboQuery.isLoading,
    systemNotePrefix,
    handleSave,
    handleClose,
    handleMoveTypeChange,
    handleTargetComboIdChange,
    handleClearTargetComboId,
    handleReasonChange,
    validateTargetCombo,
    validateReason,
  };
}
